// Pure helpers for the Canvas API explorer. The action registry they read comes from
// links/lib/lms_client/canvas_api_explorer.py (see its docstring for the fields).

const FALLBACK_COLUMNS = ['id', 'name', 'title', 'display_name', 'type']
const MAX_COLUMNS = 6
export const ROW_KEY = '__explorerRow'

export const isPlainObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

// The drilldowns an action offers on one result row (or on a single-object result).
export const drilldownsFor = (action, row) =>
  ((action && action.drilldowns) || []).filter(drill => {
    if (!isPlainObject(row)) return false
    const when = drill.when || {}
    const matches = Object.keys(when).every(field => row[field] === when[field])
    const hasValues = Object.values(drill.params).every(
      field => row[field] !== undefined && row[field] !== null && row[field] !== ''
    )
    return matches && hasValues
  })

// The next action's params, filled from the row's fields.
export const drilldownParams = (drill, row) =>
  Object.fromEntries(
    Object.entries(drill.params).map(([param, field]) => [param, String(row[field])])
  )

// Params as the action form starts them: booleans at their default, everything else empty.
export const initialParams = action =>
  Object.fromEntries(
    action.params.map(param => [
      param.name,
      param.type === 'boolean' ? param.default !== false : '',
    ])
  )

// The Canvas path an action calls, with the course and params filled in, so it can be reused
// as a custom request. Notes in parentheses ("(due_at only)") describe the action, not the path.
export const resolvedPath = (action, canvasCourseId, params = {}) =>
  action.path
    .replace(/\s*\(.*\)\s*$/, '')
    .replace(/:(\w+)/g, (match, name) =>
      name === 'course_id' ? canvasCourseId || match : params[name] || match
    )

// A short label for a row, used in breadcrumbs.
export const rowLabel = row =>
  String(
    (isPlainObject(row) &&
      (row.name || row.title || row.display_name || row.url || row.id)) ||
      ''
  )

export const describeValue = value => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// Columns for a list result: the action's own, else common identifying fields, else the first
// few scalar fields of the first row.
export const listColumns = (action, rows) => {
  const objects = rows.filter(isPlainObject)
  const present = key => objects.some(row => row[key] !== undefined)
  let keys = ((action && action.columns) || []).filter(present)
  if (!keys.length) keys = FALLBACK_COLUMNS.filter(present)
  if (!keys.length && objects.length) {
    keys = Object.keys(objects[0])
      .filter(key => !isPlainObject(objects[0][key]))
      .slice(0, MAX_COLUMNS)
  }
  return keys.length ? keys : ['value']
}

// MeTable needs a unique, non-empty identity on each row, which Canvas results don't always
// have, so each row is keyed by its position and keeps the original object for drilldowns.
export const toTableRows = (rows, columns) =>
  rows.map((row, index) => {
    const tableRow = { [ROW_KEY]: String(index), __raw: row }
    columns.forEach(key => {
      tableRow[key] = describeValue(isPlainObject(row) ? row[key] : key === 'value' ? row : '')
    })
    return tableRow
  })

// For custom requests, offer to GET <path>/<id> from a list row, which follows most Canvas
// collections down to their members (/courses -> /courses/:id).
export const memberPath = (path, row) => {
  if (!isPlainObject(row) || row.id === undefined || row.id === null) return null
  const base = path.split('?')[0].replace(/\/+$/, '')
  return `${base}/${row.id}`
}
